<template>
  <div class="account-container">
    <BaseAlert v-if="addToCartRedirect" type="caution">
      <template #message>
        In order to continue with your order, you need to create a CareerSafe account. Already have
        an account?
        <router-link
          class="signin__link"
          :to="{ name: 'SignIn', query: { redirect: route.query.redirect } }"
          >Sign In</router-link
        >.
      </template>
    </BaseAlert>
    <KCard class="account-card">
      <template #header>
        <div class="header-class">Create a New Account</div>
      </template>
      <template #content>
        <AccountForm
          :has-errors="isError && !errorIsSsoRelated"
          :error-message="errorMessage"
          :form-text="formText"
          :hide-prefix="hideAlertPrefix"
          :prefix="prefixText"
          @on-submit="onSubmit"
          ><template #default>
            <div class="button-row">
              <KButton type="submit" label="Create Account" primary :loading="isPending" /></div
          ></template>
        </AccountForm>
        <form
          method="POST"
          v-show="false"
          ref="signInForm"
          :action="`${campus_url}/authenticate.k2`"
          @submit="submitSignInForm"
        >
          <!-- email address -->
          <input type="hidden" name="email" id="email" v-model="email" v-bind="emailAttrs" />

          <!-- password -->
          <input
            type="hidden"
            name="password"
            id="password"
            v-model="password"
            v-bind="passwordAttrs"
          />

          <!-- redir hidden form field for redirection path after authentication -->
          <input type="hidden" name="redir" id="redir" v-model="redir" v-bind="redirAttrs" />
        </form>
      </template>
    </KCard>
    <BaseCardCTA title="Already have an account?">
      <template #buttons>
        <KButton
          id="signin-button"
          label="Sign In Now"
          secondary
          @click="
            {
              router.push({ name: 'SignIn', query: { redirect: route.query.redirect } });
            }
          "
        />
      </template>
    </BaseCardCTA>
  </div>

  <!-- Modals -->
  <CreateAccountSsoModal
    ref="createAccountSsoModal"
    :show-continue-link="showContinueLinkInModal"
    @on-continue-despite-sso-warning="onContinueDespiteSsoWarning"
  />
</template>

<script setup lang="ts">
import { useRoute, useRouter, RouterLink } from "vue-router";
import { useUserStore } from "@/stores/user";
import { KCard, KButton } from "@k2united/frontend_components";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import { object, boolean, string, number } from "yup";
import * as yup from "yup";
import useCreateAccountMutation from "@/api/mutations/useCreateAccountMutation";
import { computed, ref, defineAsyncComponent } from "vue";
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseCardCTA from "@/components/base/BaseCardCTA.vue";
import { AxiosError } from "axios";
import { useHead } from "@unhead/vue";
import AccountForm from "@/components/auth/AccountForm.vue";

useHead({
  title: "Create Account"
});

const CreateAccountSsoModal = defineAsyncComponent(
  () => import("../../components/auth/CreateAccountSsoModal.vue")
);
const campus_url = import.meta.env.VITE_APP_CAMPUS_URL;

const route = useRoute();
const router = useRouter();
const store = useUserStore();

const createAccountSsoModal = ref();
const showContinueLinkInModal = ref(false);
const errorIsSsoRelated = ref(false);
const continueDespiteSsoWarning = ref(false);

// Redirect to home page if already authenticated
if (store.isAuthenticated) {
  router.push({ name: "Home" });
}

const addToCartRedirect = computed(
  () =>
    route.query.redirect && route.query.redirect?.toString().match(/cart{1}|sku{1}|quantity{1}/g)
);

const { mutate, isPending, isError } = useCreateAccountMutation();

const {
  submitForm: submitSignInForm,
  setFieldValue: setSignInFieldValue,
  defineField: defineSignInField
} = useForm();
const [redir, redirAttrs] = defineSignInField("redir");
const [email, emailAttrs] = defineSignInField("email");
const [password, passwordAttrs] = defineSignInField("password");
const signInForm = ref();

const errorMessage = ref("");
const hideAlertPrefix = ref(true);
const prefixText = ref("");
const activateErrorMessage =
  "The account connected to this email address hasn’t been activated yet. Check your inbox for an activation link or <a href='authenticate-account'>use a group code from your instructor to activate your account</a>.";
const formText =
  "Get access to industry-leading professional development courses, including employability skills and OSHA-authorized safety and health training.";

const setErrorBanner = (text: string, hidePrefix: boolean, prefix: string) => {
  errorMessage.value = text;
  hideAlertPrefix.value = hidePrefix;
  prefixText.value = prefix;
};

// Form
// Note that for booleans, making them required accepts false as a valid choice.
// To require them to be checked, we need to use .oneOf([true]).
const validationSchema = toTypedSchema(
  object({
    firstName: string().required().label("First Name").default(""),
    lastName: string().required().label("Last Name").default(""),
    securityQuestion: object({
      label: string(),
      value: number().required().label("Security Question")
    }),
    securityQuestionAnswer: string().required().label("Security Question Answer").default(""),
    email: string().email().required().max(255).label("Email").default(""),
    emailConfirm: string()
      .required("Confirm email required")
      .oneOf([yup.ref("email")], "Emails do not match"),
    passwordCreate: string()
      .required("Password required")
      .max(255, "Password must not be more than 255 characters.")
      .min(8, "Password must be at least 8 characters.")
      .matches(/\d/, "Password must contain a number")
      .matches(/[A-Z]/, "Password must contain an uppercase letter")
      .matches(/[a-z]/, "Password must contain a lowercase letter")
      .matches(/^\S*$/, "Password must not contain spaces"),
    passwordConfirm: string()
      .required("Confirm password required")
      .oneOf([yup.ref("passwordCreate")], "Passwords do not match"),
    terms: boolean()
      .required()
      .oneOf([true], "This field is required")
      .label("Accept Terms")
      .default(false),
    age: boolean()
      .required()
      .oneOf([true], "This field is required")
      .label("Over 13")
      .default(false)
  })
);

const { handleSubmit } = useForm({
  validationSchema
});

const onSubmit = handleSubmit((input) => {
  // Set the hidden login form fields so the user can be authenticated upon successful mutation
  if (typeof route.query.redirect === "string") {
    setSignInFieldValue("redir", route.query.redirect);
  }
  setSignInFieldValue("email", input.email);
  setSignInFieldValue("password", input.passwordCreate);
  mutate(
    {
      accountData: {
        first_name: input.firstName,
        last_name: input.lastName,
        email: input.email,
        password: input.passwordCreate,
        security_question_id: input.securityQuestion.value,
        security_answer: input.securityQuestionAnswer,
        continue_despite_sso_warning: continueDespiteSsoWarning.value
      }
    },
    {
      onSuccess: async (response) => {
        if (response.data.needs_email_verification) {
          await setSignInFieldValue("redir", router.resolve({ name: "VerifyEmail" }).path);
        }
        signInForm.value.submit();
      },
      onError: (error) => {
        if (error instanceof AxiosError && error.response?.data.errors) {
          if (error.response?.data.errors[0].code === 4129) {
            showContinueLinkInModal.value = false;
            errorIsSsoRelated.value = true;
            openSsoModal();
          } else if (error.response?.data.errors[0].code === 4130) {
            showContinueLinkInModal.value = true;
            errorIsSsoRelated.value = true;
            openSsoModal();
          } else if (error.response?.data.errors[0].code === 4115) {
            errorIsSsoRelated.value = false;
            setErrorBanner(activateErrorMessage, false, "Account Awaiting Activation");
          } else {
            setErrorBanner(error.response.data.errors[0].message, true, "");
          }
        } else {
          errorIsSsoRelated.value = false;
          setErrorBanner("An unexpected error has occurred.", true, "");
        }
      }
    }
  );
});

const openSsoModal = () => {
  createAccountSsoModal.value.showModal();
};

const onContinueDespiteSsoWarning = () => {
  continueDespiteSsoWarning.value = true;
  onSubmit();
};
</script>

<style scoped lang="scss">
.account-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  max-width: 40rem;
}
.account-card {
  padding: 1.5rem 1rem 0.75rem 1rem;

  :deep(.k-card__body) {
    padding: 1.5rem 0 0 0;
  }

  a {
    @include link;
  }
}
.header-class {
  font-size: $font-28;
  font-weight: 600;
}

.button-row {
  display: flex;
  justify-content: end;
  padding-top: 1rem;
}
.k-button {
  width: 100%;
  @include tablet {
    width: auto;
  }
}

:deep(.checkbox-container) {
  align-items: flex-start;
  display: flex;
}
:deep(.signin__link) {
  @include link-semi-bold;
}
</style>
